import { Params } from '@b2chat/chat-center-sdk';
import { createApi } from '@reduxjs/toolkit/dist/query/react';
import {
  loadAvailableMessagingAccounts,
  onCheckWhatsAppAccount,
} from '@src/actions/contact';
import B2ChatClient, { apiResource, b2chatBaseQuery } from '@src/client';
import { TakeChatFromCallStatus } from '@src/components/StartContactConversation/StartContactCall/StartContactCallFromBotDialog';
import { MessagingProvider } from '@src/model/frontendmodel';
import type {
  BotAccount,
  TemplatedMsgWrapper,
  WhatsAppTemplate,
} from '@src/types';
import { isEqual } from 'lodash';

export type GetContactsRequest = {
  filter: {
    createdAtFrom: string;
    createdAtTo: string;
    searchFilter: string;
    onlyBlacklisted: boolean;
    searchByLastUpdate: boolean;
    companyFilter: string;
    tagFilter: string[];
    assignTo: unknown;
  };
  sorting: [];
  limit: number;
  offset: number;
};

export type GetContactsResponseData = {
  id: number;
  fullName: string;
  identificationNumber: string | null;
  email: string | null;
  phoneNumber: string | null;
  mobileNumber?: string;
  country: string | null;
  province: string | null;
  city: string | null;
  cityName: string;
  address: string | null;
  company: string | null;
  remarks: string | null;
  isNewContact: boolean;
  lastUpdate: number;
  createAt: number;
  habeasdata: boolean;
  fullTextHabeasData: string | null;
  ipClient: string | null;
  channel: string | null;
  attributes: string | null;
  blacklisted: boolean;
  totalCount: number;
  tags: string[];
  assignType: string | null;
  assignDescription: string | null;
  waUsername: string | null;
  waUserId: string | null;
};

export type GetContactsResponse = {
  data: GetContactsResponseData[];
  paging: {
    hasMoreItems: boolean;
  };
};
export type GetContactsCountResponse = {
  count: number;
};

export type RequestContactPhoneNumberRequest = {
  contactId: number;
  bsuid: string;
  botAccountId: number;
  templateName: string;
  templateLanguage: string;
};

export type RequestContactPhoneNumberResponse = {
  waPhoneRequestStatus: string;
  broadcastResponse: string;
};

export type GetContactCurrentChatResponse = {
  id: string; // chatId
  status: TakeChatFromCallStatus;
  assignedTo: {
    id: number;
    fullName: string;
    mobileNumber?: string;
    email: string;
  };
  contact: {
    id: number;
    fullName: string;
    cmaId: string;
  };
  botAccount: {
    id: number;
    username: string;
    provider: MessagingProvider;
  };
};

const contactsApi = createApi({
  reducerPath: 'contactsApi',
  baseQuery: b2chatBaseQuery,
  tagTypes: ['contacts'],
  endpoints: ({ query, mutation }) => ({
    getContacts: query<GetContactsResponse, GetContactsRequest>({
      providesTags: ['contacts'],
      query: request =>
        apiResource(B2ChatClient.resources.contacts.actions.v2GetContacts, {
          data: request,
        }),
    }),
    getContactsCount: query<GetContactsCountResponse, GetContactsRequest>({
      providesTags: ['contacts'],
      serializeQueryArgs: args => args,
      forceRefetch: params => !isEqual(params.currentArg, params.previousArg),
      query: request => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { offset, ...requestWithoutOffset } = request;

        return apiResource(
          B2ChatClient.resources.contacts.actions.v2GetContactsCount,
          {
            data: requestWithoutOffset,
          }
        );
      },
    }),
    checkWhatsAppAccount: query<boolean, { contactId: string }>({
      query: ({ contactId }) =>
        apiResource(B2ChatClient.resources.contacts.actions.checkAccountWS, {
          params: { id: contactId },
          transformError: () => ({ data: false }),
          transformResponse: () => true,
        }),
      async onQueryStarted(_data, { dispatch, queryFulfilled }) {
        const { data: isWhatsAppAccount } = await queryFulfilled;

        dispatch(onCheckWhatsAppAccount(isWhatsAppAccount));
      },

      extraOptions: {
        maxRetries: 0,
      },
    }),
    getAvailableMessagingAccounts: query<BotAccount[], { contactId: string }>({
      query: ({ contactId }) =>
        apiResource(B2ChatClient.resources.contacts.actions.getBotAccounts, {
          params: { id: contactId },
        }),
      async onQueryStarted(data, { dispatch, queryFulfilled }) {
        const { data: botAccounts } = await queryFulfilled;

        dispatch(
          loadAvailableMessagingAccounts(botAccounts, { id: data.contactId })
        );
      },
    }),
    getContactTemplates: query<
      TemplatedMsgWrapper[],
      { botAccountId: number; contactId: number }
    >({
      query: ({ botAccountId, contactId }) =>
        apiResource(B2ChatClient.resources.templates.actions.getTemplates, {
          params: {
            botAccountId,
            contactId,
            callPermissionRequestOnly: true,
          } as unknown as Params,
        }),
      transformResponse: (response: TemplatedMsgWrapper[]) =>
        // Sort templates alphabetically by name
        [...response].sort((a, b) => {
          const nameA = a.templatedMsg.name || '';
          const nameB = b.templatedMsg.name || '';
          return nameA.localeCompare(nameB);
        }),
    }),
    getCurrentChat: query<
      GetContactCurrentChatResponse,
      {
        contactId: string;
        'bot-id': string;
        'via-bot-account': string;
        'via-msg-provider': string;
        'contact-account': string;
      }
    >({
      serializeQueryArgs: args => args,
      forceRefetch: params => !isEqual(params.currentArg, params.previousArg),
      query: params =>
        apiResource(B2ChatClient.resources.contacts.actions.getCurrentChat, {
          params,
        }),
    }),
    getPhoneRequestTemplates: query<
      WhatsAppTemplate[],
      { botAccountId: number }
    >({
      query: ({ botAccountId }) =>
        apiResource(
          B2ChatClient.resources.templates.actions
            .getTemplatesRequestPhoneNumber,
          {
            params: { botAccountId },
          }
        ),
    }),
    requestContactPhoneNumber: mutation<
      RequestContactPhoneNumberResponse,
      RequestContactPhoneNumberRequest
    >({
      query: ({ contactId, ...data }) =>
        apiResource(
          B2ChatClient.resources.contacts.actions.requestContactPhoneNumber,
          {
            params: { contactId },
            data,
          }
        ),
    }),
  }),
});

export default contactsApi;

export const {
  useGetContactsQuery,
  useLazyGetContactsQuery,
  useLazyGetAvailableMessagingAccountsQuery,
  useLazyCheckWhatsAppAccountQuery,
  useGetContactTemplatesQuery,
  useGetPhoneRequestTemplatesQuery,
  useGetCurrentChatQuery,
  useLazyGetCurrentChatQuery,
  useRequestContactPhoneNumberMutation,
} = contactsApi;
